import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { startMainThreadDrainTriggers } from "./lib/drain-runner";
import { registerServiceWorker } from "./lib/register-sw";

// Sentry error tracking (production-only). When VITE_SENTRY_DSN is unset the
// SDK is never initialised and no events are sent. In production the DSN and
// auth token are fetched from SSM at CI build time (deploy-production-frontend.yml).
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: "production",
    release: import.meta.env.VITE_APP_BUILD,
    tracesSampleRate: 0.1,
    beforeSend(event) {
      // Strip query strings from the request URL so that free-text search
      // params (e.g. ?q=Jane+Doe) containing PII are never sent to Sentry.
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url);
          url.search = "";
          event.request.url = url.toString();
        } catch {
          // Unparseable URL — leave it as-is rather than dropping the event.
        }
      }
      // Same scrub for navigation breadcrumbs that record the URL at the time
      // of the error (Breadcrumbs integration captures address-bar changes).
      if (event.breadcrumbs?.values) {
        for (const crumb of event.breadcrumbs.values) {
          if (crumb.data?.url) {
            try {
              const u = new URL(crumb.data.url as string);
              u.search = "";
              crumb.data.url = u.toString();
            } catch {
              // ignore
            }
          }
        }
      }
      return event;
    },
  });
}

// Build stamp (#420 regression hook). Referencing VITE_APP_BUILD inlines its literal into every
// build, which serves two purposes for the stale-precached-shell regression: (a) it gives the
// two-generation SW-update E2E a DOM-readable marker (`<html data-app-build>`) to prove a stale
// precached shell was replaced by the freshly-deployed one, and (b) it guarantees two deploy
// generations emit byte-different bundles so Workbox actually detects a waiting update. A harmless
// no-op stamp ("dev") in ordinary builds where the var is unset.
document.documentElement.dataset.appBuild = import.meta.env.VITE_APP_BUILD ?? "dev";

createRoot(document.getElementById("root")!).render(<App />);

// PWA Service Worker (Story 7.2). Self-gated (PROD || VITE_PWA_DEV) and non-blocking — render
// is never gated on registration.
registerServiceWorker();

// Offline sync (Story 7.4). Drain the outbound queue on the `online` event and on access-token
// promotion — the main thread is the sole drain driver. Registration is safe this early because
// every pass is token-gated (gh-634): nothing touches the network until the boot silent-refresh
// (or a login) actually mints the bearer the replays must carry.
startMainThreadDrainTriggers();
